import { useEffect, useState } from 'react';
import { ArrowUpRight, ShieldCheck, RefreshCw, LogOut } from 'lucide-react';
import { api, post } from './api';
import { text, type Locale } from '../shared/content';
import { errorText } from './i18n';
import PrivacyPanel from './PrivacyPanel';
const copy = {
  title: text(
    'Your account, your next step',
    '账号支持与下一步',
    '帳號支援與下一步',
    'Tu cuenta, tu siguiente paso',
  ),
  info: text(
    'A gameplay restriction does not remove your right to request a platform review. Elected officers cannot review appeals.',
    '游戏权限受限时，仍可向平台申请复核。玩家公职人员无权处理申诉。',
    '遊戲權限受限時，仍可向平台申請複核。玩家公職人員無權處理申訴。',
    'Una restricción del juego no impide solicitar revisión. Los cargos electos no resuelven apelaciones.',
  ),
  active: text(
    'Your account has no active gameplay restriction.',
    '账号目前没有有效的游戏权限限制。',
    '帳號目前沒有有效的遊戲權限限制。',
    'Tu cuenta no tiene restricciones activas.',
  ),
  restricted: text(
    'Gameplay temporarily restricted',
    '游戏权限暂时受限',
    '遊戲權限暫時受限',
    'Acceso al juego restringido temporalmente',
  ),
  expires: text('Restriction ends', '限制到期时间', '限制到期時間', 'Fin de la restricción'),
  indefinite: text('Pending platform review', '等待平台复核', '等待平台複核', 'Pendiente de revisión'),
  appeal: text('Ask for a review', '提交申诉', '提交申訴', 'Solicitar revisión'),
  placeholder: text(
    'Explain what happened and why you are requesting a review · 5–2000 characters',
    '说明发生了什么，以及申请复核的原因 · 5–2000 字',
    '說明發生了什麼，以及申請複核的原因 · 5–2000 字',
    'Explica lo ocurrido y por qué solicitas revisión · 5–2000 caracteres',
  ),
  limit: text(
    'One open appeal per account; at most three submissions per 30 days. Do not include passwords or payment details.',
    '每个账号可有一条待处理申诉，每 30 天最多提交三次。请勿填写密码或支付信息。',
    '每個帳號可有一則待處理申訴，每 30 天最多提交三次。請勿填寫密碼或付款資訊。',
    'Una apelación abierta por cuenta y hasta tres cada 30 días. No incluyas contraseñas ni datos de pago.',
  ),
  history: text('Your review requests', '你的申诉记录', '你的申訴記錄', 'Tus solicitudes'),
  open: text('Waiting for review', '等待处理', '等待處理', 'Pendiente'),
  lift: text('Restriction lifted', '限制已解除', '限制已解除', 'Restricción retirada'),
  uphold: text('Restriction maintained', '维持原限制', '維持原限制', 'Restricción mantenida'),
  back: text('Return to my city', '返回我的城市', '返回我的城市', 'Volver a mi ciudad'),
  refresh: text('Refresh status', '刷新状态', '重新整理狀態', 'Actualizar estado'),
  signout: text('Sign out', '退出登录', '登出', 'Cerrar sesión'),
  sent: text('Your review request is saved.', '申诉已保存。', '申訴已儲存。', 'Tu solicitud está guardada.'),
};
type Status = {
  restriction: { reason: string; expires_at: string | null } | null;
  appeals: {
    id: string;
    body: string;
    status: string;
    decision: string | null;
    note: string | null;
    created_at: string;
  }[];
};
export default function AccountSupport({
  locale,
  onClose,
  onSignOut,
}: {
  locale: Locale;
  onClose: () => Promise<void> | void;
  onSignOut: () => Promise<void>;
}) {
  const t = (k: keyof typeof copy) => copy[k][locale];
  const [status, setStatus] = useState<Status>(),
    [body, setBody] = useState(''),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  const refresh = async () => {
    try {
      setStatus(await api('/account'));
    } catch (e) {
      setMessage(errorText(locale, e));
    }
  };
  useEffect(() => {
    let alive = true;
    const read = () =>
      api<Status>('/account')
        .then((s) => {
          if (alive) setStatus(s);
        })
        .catch((e) => {
          if (alive) setMessage(errorText(locale, e));
        });
    read();
    const timer = setInterval(read, 30000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);
  return (
    <div className="public-share-page account-support">
      <a className="public-share-brand" href="/">
        AMERICAN DREAM <small>ONLINE · NEW YORK</small>
      </a>
      <section>
        <ShieldCheck size={30} />
        <h2>{t('title')}</h2>
        <p className="muted small">{t('info')}</p>
        {status?.restriction ? (
          <div className="account-restriction">
            <h3>{t('restricted')}</h3>
            <p>{status.restriction.reason}</p>
            <small>
              {t('expires')} ·{' '}
              {status.restriction.expires_at
                ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
                    new Date(status.restriction.expires_at),
                  )
                : t('indefinite')}
            </small>
          </div>
        ) : status ? (
          <p className="green">{t('active')}</p>
        ) : (
          <p>…</p>
        )}
        {status?.restriction && !status.appeals.some((a) => a.status === 'open') && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await post('/appeals', { body });
                setBody('');
                setMessage(t('sent'));
                await refresh();
              } catch (e) {
                setMessage(errorText(locale, e));
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              {t('appeal')}
              <textarea
                aria-label={t('appeal')}
                placeholder={t('placeholder')}
                minLength={5}
                maxLength={2000}
                required
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </label>
            <p className="muted tiny">{t('limit')}</p>
            <button className="button primary full" disabled={busy || body.trim().length < 5}>
              {t('appeal')}
              <ArrowUpRight size={15} />
            </button>
          </form>
        )}
        {!!status?.appeals.length && (
          <>
            <h3>{t('history')}</h3>
            {status.appeals.map((a) => (
              <article className="appeal-record" key={a.id}>
                <strong>
                  {a.status === 'open' ? t('open') : a.decision === 'lift' ? t('lift') : t('uphold')}
                </strong>
                <p>{a.body}</p>
                {a.note && <blockquote>{a.note}</blockquote>}
                <small>
                  {new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(a.created_at))}
                </small>
              </article>
            ))}
          </>
        )}
        {message && (
          <p className="form-error" role="status">
            {message}
          </p>
        )}
        <button className="button soft full" onClick={refresh}>
          <RefreshCw size={14} />
          {t('refresh')}
        </button>
        {status && !status.restriction && (
          <button
            className="button primary full"
            onClick={async () => {
              try {
                await onClose();
              } catch (e) {
                setMessage(errorText(locale, e));
              }
            }}
          >
            {t('back')}
          </button>
        )}
        <button
          className="text-button"
          onClick={async () => {
            try {
              await onSignOut();
            } catch (e) {
              setMessage(errorText(locale, e));
            }
          }}
        >
          <LogOut size={13} />
          {t('signout')}
        </button>
      </section>
      <PrivacyPanel locale={locale} />
    </div>
  );
}
